import Input from "@ui/input/Input";
import { Select } from "@ui/select/Select";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Box, HBox, VBox } from "@ui/box/Box";
import { PositiveSizeChart } from "@ui/chart/PositiveSizeChart";
import { Page } from "@ui/page/Page";
import { Typo } from "@ui/text/Typo";
import { formatCurrencyWithNumberToSymbolWithNumber } from "@ui/utils/formatter";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  amountValidationError,
  parsePositiveAmountToMinorUnits,
} from "src/utils/amounts";
import { getFriendlyErrorMessage } from "src/utils/errors";
import { GD } from "src/GD";
import { USER_ROLE } from "src/services/AuthService";
import { useCurrentPath, useRouter } from "src/services/Router";
import { StatusBadge } from "../components/StatusBadge";
import { S_UNIVERSAL_DIALOG } from "../components/universalDialog/signals";
import { FormParser } from "src/view/FormParser";
import { UserDetails } from "../components/UserDetails";
import { IconInfo } from "@ui/icons/IconInfo";
import { DEFAULT_CURRENCY, MAX_USER_DETAILS_WIDTH } from "src/constants";
import { getTeamUserDisableSuccessMessage } from "src/utils/teamBalances";
import { confirmAction } from "../popup/general/ConfirmDialog";
import {
  buildBuyerCardFinancials,
  buildBuyerSpendMap,
} from "src/utils/buyerFinancials";
import { ActionButton, Avatar, DS, EntityHeader, Section, Stage, StageButton, StageStat, FormIntro, Glyph } from "../ds/Kit";
import { TeamMemberCard } from "../components/TeamMemberCard";
import { useWindowSize } from "@ui/utils/useResize";
import { isMobileViewport } from "@ui/utils/mobileViewport";
import { useIssuer } from "../issuer/IssuerProvider";
import { getTransactionDisplayLabel } from "src/utils/transactionPresentation";

const TEAM_ANALYTICS_DAYS = 7;
const TEAM_TOP_SPENDERS_LIMIT = 5;
const TEAM_BUYER_STATS_LIMIT = 1000;
const TEAM_ANALYTICS_DAY_OPTIONS = [7, 14, 30];
const TEAM_TOP_SPENDERS_LIMIT_OPTIONS = [5, 10, 15];
const TEAM_RECENT_ACTIVITY_LIMIT = 10;
const BAR_RADIUS = 4;
const TEAM_DETAILS_BRAND = "#2B5C8A";
const TEAM_DETAILS_TEXT = "#17243B";
const TEAM_DETAILS_MUTED = "#617087";
const TEAM_DETAILS_BORDER = "#E3E7EF";
const TEAM_DETAILS_SOFT = "#F8FAFC";
const TEAM_DETAILS_SERIES_COLORS = [
  TEAM_DETAILS_BRAND,
  "#dc2626",
  "#10b981",
  "#f59e0b",
  "#3f6e9a",
  "#06b6d4",
];

type DeclinedBuyerChartPoint = {
  name: string;
  fullDate: string;
  total: number;
  [key: string]: string | number;
};

type DeclinedBuyerSeries = {
  key: string;
  name: string;
  total: number;
  color: string;
};

export const TeamDetailsPage = () => {
  const pathInfo = useCurrentPath();
  const router = useRouter();
  const [showAllRecent, setShowAllRecent] = useState(false);
  const { selectedIssuer, selectedIssuerId } = useIssuer();
  const issuerCurrency = selectedIssuer?.currency || DEFAULT_CURRENCY;
  const { width: viewportWidth, height: viewportHeight } = useWindowSize();
  const isMobile = isMobileViewport(viewportWidth, viewportHeight);
  const teamId = pathInfo?.currentParams?.teamId;
  const [analyticsDays, setAnalyticsDays] = useState(TEAM_ANALYTICS_DAYS);
  const [topSpendersLimit, setTopSpendersLimit] = useState(
    TEAM_TOP_SPENDERS_LIMIT,
  );
  const [declinedBuyerHidden, setDeclinedBuyerHidden] = useState<
    Record<string, boolean>
  >({});
  const teamAnalyticsParams = teamId
    ? { uid: teamId, currency: issuerCurrency, issuer: selectedIssuerId ?? undefined }
    : undefined;

  const [profile] = GD.REQ_PROFILE.useRequest();
  const [cardsData] = GD.REQ_CARDS.useRequest();
  const [teamResponse, updateTeam, , teamBusy] = GD.REQ_TEAM_GET.useRequest(
    undefined,
    teamId ? teamId : undefined,
  );
  const [membersResponse, updateMembers, , membersBusy] =
    GD.REQ_TEAM_USERS.useRequest(
      undefined,
      teamId ? { uid: teamId } : undefined,
    );
  const [
    teamTransactionsResponse,
    updateTeamTransactions,
    ,
    teamTransactionsBusy,
  ] = GD.REQ_COMPANY_TRANSACTIONS_ALL.useRequest(
    undefined,
    teamId
      ? { team_uid: teamId, page: 0, limit: TEAM_RECENT_ACTIVITY_LIMIT }
      : undefined,
  );
  const [moneyInflowResponse, updateMoneyInflow, , moneyInflowBusy] =
    GD.REQ_TEAM_STATS_MONEY_INFLOW.useRequest(
      undefined,
      teamAnalyticsParams
        ? { ...teamAnalyticsParams, days: analyticsDays }
        : undefined,
    );
  const [topSpendersResponse, updateTopSpenders, , topSpendersBusy] =
    GD.REQ_TEAM_STATS_TOP_SPENDERS.useRequest(
      undefined,
      teamAnalyticsParams
        ? { ...teamAnalyticsParams, limit: TEAM_BUYER_STATS_LIMIT }
        : undefined,
    );
  const [
    transactionStatusResponse,
    updateTransactionStatus,
    ,
    transactionStatusBusy,
  ] = GD.REQ_TEAM_STATS_TRANSACTION_STATUS.useRequest(
    undefined,
    teamAnalyticsParams
      ? { ...teamAnalyticsParams, days: analyticsDays }
      : undefined,
  );
  const [
    declinedByBuyerResponse,
    updateDeclinedByBuyer,
    ,
    declinedByBuyerBusy,
  ] = GD.REQ_STATS_DECLINED_BY_BUYER.useRequest(
    undefined,
    teamId ? { days: analyticsDays, team_uid: teamId } : undefined,
  );

  const refreshTeamAnalytics = (uid: string) => {
    updateTeamTransactions({
      team_uid: uid,
      page: 0,
      limit: TEAM_RECENT_ACTIVITY_LIMIT,
    });
    updateMoneyInflow({
      uid,
      days: analyticsDays,
      currency: issuerCurrency,
      issuer: selectedIssuerId ?? undefined,
    });
    updateTopSpenders({
      uid,
      limit: TEAM_BUYER_STATS_LIMIT,
      currency: issuerCurrency,
      issuer: selectedIssuerId ?? undefined,
    });
    updateTransactionStatus({
      uid,
      days: analyticsDays,
      currency: issuerCurrency,
      issuer: selectedIssuerId ?? undefined,
    });
    updateDeclinedByBuyer({ days: analyticsDays, team_uid: uid });
  };

  useEffect(() => {
    if (!teamId) return;

    updateTeam(teamId);
    updateMembers({ uid: teamId });
    refreshTeamAnalytics(teamId);
    GD.REQ_CARDS.request();
    // eslint-disable-next-line
  }, [teamId, analyticsDays, issuerCurrency, selectedIssuerId]);

  const team = teamResponse?.data;
  const members = useMemo(() => membersResponse?.data || [], [membersResponse]);

  const [filter, setFilter] = useState({ search: "", role: "All" });

  const filteredMembers = useMemo(() => {
    return members.filter((m) => {
      const matchesSearch =
        filter.search === "" ||
        m.name.toLowerCase().includes(filter.search.toLowerCase()) ||
        m.email.toLowerCase().includes(filter.search.toLowerCase());
      const matchesRole =
        filter.role === "All" || m.company_role === filter.role;
      return matchesSearch && matchesRole;
    });
  }, [members, filter]);

  const chartData = useMemo(() => {
    return (moneyInflowResponse?.data?.daily || []).map((point) => ({
      date: new Date(point.date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
      amount: Number(point.amount || 0),
    }));
  }, [moneyInflowResponse]);
  const hasMoneyInflow = chartData.some(point => point.amount !== 0);

  const spendingData = useMemo(() => {
    return (topSpendersResponse?.data?.buyers || [])
      .slice(0, topSpendersLimit)
      .map((buyer) => ({
        userUid: buyer.user_uid,
        name: buyer.user_name,
        amount: Number(buyer.amount || 0),
      }));
  }, [topSpendersLimit, topSpendersResponse]);
  const buyerSpend = useMemo(
    () => buildBuyerSpendMap(topSpendersResponse?.data?.buyers || []),
    [topSpendersResponse],
  );
  const buyerCardFinancials = useMemo(
    () => buildBuyerCardFinancials((cardsData?.data || []) as ICardSaved[]),
    [cardsData?.data],
  );

  const statusData = useMemo(() => {
    const colors: Record<string, string> = {
      completed: "#10B981",
      pending: "#F59E0B",
      error: "#EF4444",
    };

    return (transactionStatusResponse?.data?.statuses || []).map((entry) => ({
      name: entry.status,
      value: Number(entry.count || 0),
      amount: Number(entry.amount || 0),
      color: colors[entry.status] || "#6B7280",
    }));
  }, [transactionStatusResponse]);
  const declinedBuyerChart = useMemo(() => {
    const sortedBuyers = (declinedByBuyerResponse?.data?.buyers || [])
      .map((buyer) => ({
        key: buyer.buyer_uid,
        name: buyer.buyer_name || "Unknown Buyer",
        daily: buyer.daily,
        total: buyer.daily.reduce(
          (sum, point) => sum + Number(point.count || 0),
          0,
        ),
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 4);

    const series: DeclinedBuyerSeries[] = sortedBuyers.map((buyer, index) => ({
      key: buyer.key,
      name: buyer.name,
      total: buyer.total,
      color:
        TEAM_DETAILS_SERIES_COLORS[index % TEAM_DETAILS_SERIES_COLORS.length],
    }));
    const dateMap = new Map<string, DeclinedBuyerChartPoint>();

    sortedBuyers.forEach((buyer) => {
      buyer.daily.forEach((point) => {
        const pointDate = new Date(`${point.date}T00:00:00`);
        const existing =
          dateMap.get(point.date) ||
          ({
            name: pointDate.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            }),
            fullDate: pointDate.toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            }),
            total: 0,
          } as DeclinedBuyerChartPoint);
        const count = Number(point.count || 0);
        existing[buyer.key] = count;
        existing.total = Number(existing.total || 0) + count;
        dateMap.set(point.date, existing);
      });
    });

    const daily = Array.from(dateMap.entries())
      .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
      .map(([, point]) => {
        series.forEach((buyer) => {
          if (point[buyer.key] === undefined) point[buyer.key] = 0;
        });
        return point;
      });

    return {
      daily,
      series,
      total: series.reduce((sum, buyer) => sum + buyer.total, 0),
    };
  }, [declinedByBuyerResponse]);
  const moneyInflowCurrency =
    moneyInflowResponse?.data?.currency || issuerCurrency;
  const topSpendersCurrency =
    topSpendersResponse?.data?.currency || issuerCurrency;
  const transactionStatusCurrency =
    transactionStatusResponse?.data?.currency || issuerCurrency;
  const moneyInflowTotal = Number(moneyInflowResponse?.data?.total || 0);
  const topSpendersTotal = Number(topSpendersResponse?.data?.total || 0);
  const transactionStatusTotalCount = Number(
    transactionStatusResponse?.data?.total_count || 0,
  );
  const transactionStatusTotalAmount = Number(
    transactionStatusResponse?.data?.total_amount || 0,
  );
  const hasTopSpendersData = spendingData.some((entry) => entry.amount > 0);
  const hasTransactionStatusData = statusData.some((entry) => entry.value > 0);
  const visibleDeclinedBuyerSeries = declinedBuyerChart.series.filter(
    (series) => !declinedBuyerHidden[series.key],
  );
  const visibleDeclinedBuyerTotal = visibleDeclinedBuyerSeries.reduce(
    (sum, series) => sum + series.total,
    0,
  );
  const hasDeclinedBuyerData =
    declinedBuyerChart.series.length > 0 && declinedBuyerChart.total > 0;
  const toggleDeclinedBuyerSeries = (key: string) => {
    setDeclinedBuyerHidden((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Stats calculation
  const teamStats = useMemo(() => {
    const stats = {
      buyersCount: 0,
      teamLeadsCount: 0,
      totalBalance: 0,
      mostMoneyUser: null as ITeamUser | null,
      maxBalance: -1,
    };

    members.forEach((m) => {
      if (m.company_role === USER_ROLE.BUYER) {
        stats.buyersCount++;
        const balance = m.team_balance || 0;
        stats.totalBalance += balance;

        if (balance > stats.maxBalance) {
          stats.maxBalance = balance;
          stats.mostMoneyUser = m;
        }
      }
      if (m.company_role === USER_ROLE.TEAM_LEAD) stats.teamLeadsCount++;
    });

    return stats;
  }, [members]);

  const recentTransactions = useMemo(
    () =>
      (teamTransactionsResponse?.data?.transactions || []).slice(
        0,
        TEAM_RECENT_ACTIVITY_LIMIT,
      ),
    [teamTransactionsResponse],
  );

  const handleAddMember = async () => {
    if (!team) return;
    const usersRes = await GD.REQ_USERS_LIST_TEAM.request({
      team_uid: team.uid,
    });
    if (!usersRes.data) {
      toast.error("Could not load users list");
      return;
    }

    const existingIds = new Set(members.map((m) => m.uid));
    const availableUsers = usersRes.data
      .filter((u) => !existingIds.has(u.uid))
      .map((u) => ({
        name: `${u.name} (${u.email}) - ${u.company_role}`,
        value: u.uid,
      }));

    S_UNIVERSAL_DIALOG.invoke({
      label: "Add Member to Team",
      contentPadding: isMobile ? "16px 16px 0" : "24px",
      content: (
        <>
          <FormIntro icon={Glyph.invite} title="Add a team member" description="Pick a user to join this team. They get access right away." />
          <FormParser
            onSubmit={async (data) => {
              const res = await GD.REQ_TEAM_USER_ADD.request({
                uid: team.uid,
                user_uid: data.user_uid,
                team_uid: team.uid,
              });

              if (res.error) return res;

              toast.success("User added successfully");
              updateMembers({ uid: team.uid });
              S_UNIVERSAL_DIALOG.invoke(null);
              return {};
            }}
            onRequest={async () => {
              return {
                user_uid: "",
                $__user_uid_label: "Member",
                $__user_uid_type: "select",
                $__user_uid_values: availableUsers,
                $__decorator: { title: "Select User" },
              };
            }}
            primaryButton={{ title: "Add" }}
            secondaryButton={{
              title: "Cancel",
              onClick: () => {
                S_UNIVERSAL_DIALOG.invoke(null);
              },
            }}
          />
        </>
      ),
      closeDialog: () => {
        S_UNIVERSAL_DIALOG.invoke(null);
      },
    });
  };

  const handleDisableUser = async (user_uid: string) => {
    if (!teamId) return;
    const member = members.find(candidate => candidate.uid === user_uid);

    if (
      !(await confirmAction({
        title: "Remove team member",
        message: `Remove ${member?.name || "this member"} from the team?${member?.company_role === USER_ROLE.BUYER ? " Their remaining team balance will be returned to the company account." : ""}`,
        confirm: "Remove member",
      }))
    )
      return;

    const response = await GD.REQ_TEAM_USER_DISABLE.request({
      uid: teamId,
      user_uid,
    });
    if (response.error) {
      toast.error(
        getFriendlyErrorMessage(response.error, "Failed to update user status"),
      );
      return;
    }
    toast.success(getTeamUserDisableSuccessMessage(response.data.returned));
    updateMembers({ uid: teamId });
    updateTeam(teamId);
    refreshTeamAnalytics(teamId);
  };

  const handleTopUp = (user: ITeamUser) => {
    if (!teamId) return;
    S_UNIVERSAL_DIALOG.invoke({
      label: `Top Up ${user.name}`,
      content: (
        <FormParser
          key="teamUserTopUp"
          onSubmit={async (data) => {
            const parsedAmount = parsePositiveAmountToMinorUnits(data.amount);
            if (typeof parsedAmount.amount !== "number")
              return amountValidationError(
                parsedAmount.error || "Enter a valid amount.",
              );
            const res = await GD.REQ_TEAM_USER_TOPUP.request({
              uid: teamId, // Using teamId from closure
              user_uid: user.uid,
              amount: parsedAmount.amount,
              currency: issuerCurrency,
              issuer: selectedIssuerId ?? undefined,
            });

            if (res.error) return res;

            toast.success(`Top up for ${user.name} successful`);
            S_UNIVERSAL_DIALOG.invoke(null);
            GD.REQ_COMPANIES.request({ force: true });
            GD.REQ_TEAMS_LIST.request();
            updateTeam(teamId);
            updateMembers({ uid: teamId });
            refreshTeamAnalytics(teamId);

            return {};
          }}
          onRequest={async () => {
            const template = await GD.REQ_USER_TOPUP_TEMPLATE.request();
            return template.data!;
          }}
          primaryButton={{ title: "Top Up" }}
          secondaryButton={{
            title: "Cancel",
            onClick: () => {
              S_UNIVERSAL_DIALOG.invoke(null);
            },
          }}
        />
      ),
      closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null),
    });
  };

  const handleUserClick = async (user_uid: string) => {
    S_UNIVERSAL_DIALOG.invoke({
      sx: { maxWidth: MAX_USER_DETAILS_WIDTH },
      contentPadding: "0px",
      content: (
        <UserDetails
          userUid={user_uid}
          teamUid={teamId}
          onClose={() => S_UNIVERSAL_DIALOG.invoke(null)}
          onUpdate={() => {
            if (teamId) updateMembers({ uid: teamId });
          }}
        />
      ),
      closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null),
    });
  };

  if (teamBusy && !team) {
    return (
      <Page sx={{ boxShadow: "none" }} label="Team Details">
        <Box
          sx={{
            padding: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typo variant="headMedium" sx={{ color: "#6B7280" }}>
            Loading team...
          </Typo>
        </Box>
      </Page>
    );
  }

  if (!team) {
    return (
      <Page sx={{ boxShadow: "none" }} label="Team Details">
        <Box sx={{ padding: "40px" }}>
          <Typo sx={{ color: "#6B7280" }}>Team not found</Typo>
        </Box>
      </Page>
    );
  }

  // ─── Derived display values ───────────────────────────────────────────────
  const createdDate = team.created_at
    ? new Date(team.created_at).toLocaleDateString("en-GB", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      })
    : null;
  const canManageTeam =
    profile?.company_role === USER_ROLE.TEAM_LEAD ||
    profile?.company_role === USER_ROLE.ADMIN;
  const activeMembers = members.filter((m) => m.team_status === "active").length;
  const spendLoaded = Boolean(topSpendersResponse?.data);
  const cardsLoaded = Boolean(cardsData?.data);
  const formatMoney = (amount: number | undefined, currency = issuerCurrency) =>
    amount === undefined
      ? "—"
      : formatCurrencyWithNumberToSymbolWithNumber(amount, currency);
  const hasActiveFilter = filter.search !== "" || filter.role !== "All";

  const handleEditTeam = () => {
    S_UNIVERSAL_DIALOG.invoke({
      label: "Edit team",
      contentPadding: isMobile ? "16px 16px 0" : "24px",
      content: (
        <FormParser
          onSubmit={async (data) => {
            const res = await GD.REQ_TEAM_UPDATE.request({
              ...team,
              name: data.name,
            });
            if (res.error) return res;

            toast.success("Team updated");
            updateTeam(team.uid);
            GD.REQ_TEAMS_LIST.request();
            S_UNIVERSAL_DIALOG.invoke(null);
            return {};
          }}
          onRequest={async () => ({
            name: team.name,
            $__decorator: { title: "Edit Team Details" },
          })}
          primaryButton={{ title: "Save Changes" }}
          secondaryButton={{
            title: "Cancel",
            onClick: () => S_UNIVERSAL_DIALOG.invoke(null),
          }}
        />
      ),
      closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null),
    });
  };


  // ─── Shared chart panel style ─────────────────────────────────────────────
  const CHART_PANEL_SX = {
    backgroundColor: DS.surface,
    borderRadius: DS.radius,
    border: `1px solid ${DS.line}`,
    boxShadow: DS.shadow,
    padding: isMobile ? "18px 16px" : "24px",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  } as const;

  const renderChartMessage = (message: string) => (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
      }}
    >
      <IconInfo width={32} height={32} color="#D1D5DB" />
      <Typo variant="medium" sx={{ color: "#9CA3AF", textAlign: "center" }}>
        {message}
      </Typo>
    </Box>
  );

  const renderSegmentedControl = (
    options: number[],
    value: number,
    onChange: (nextValue: number) => void,
    suffix: string,
  ) => (
    <HBox
      sx={{
        border: `1px solid ${TEAM_DETAILS_BORDER}`,
        borderRadius: "8px",
        overflow: "hidden",
        backgroundColor: TEAM_DETAILS_SOFT,
      }}
    >
      {options.map((option) => {
        const active = option === value;
        return (
          <Box
            key={option}
            element="button"
            type="button"
            aria-label={`Show ${suffix === "d" ? "last " : "top "}${option}${suffix === "d" ? " days" : " spenders"}`}
            aria-pressed={active}
            onClick={() => onChange(option)}
            sx={{
              padding: "8px 12px",
              minHeight: "44px",
              border: "none",
              fontFamily: "inherit",
              cursor: "pointer",
              backgroundColor: active ? TEAM_DETAILS_BRAND : "transparent",
              color: active ? "#FFFFFF" : TEAM_DETAILS_MUTED,
              fontSize: "11px",
              fontWeight: 700,
              lineHeight: 1,
              transition: "all 0.15s ease",
            }}
          >
            {option}
            {suffix}
          </Box>
        );
      })}
    </HBox>
  );

  const MEMBER_STATS = ["Available", "Spent", "Cards", "On cards"];
  const stroke = { fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" } as const;

  const renderMemberRow = (row: ITeamUser) => {
    const isBuyer = (row.company_role || "").toLowerCase().includes("buyer");
    const cardFinancials = buyerCardFinancials.get(row.uid);
    const showTopUp = isBuyer && profile?.company_role !== USER_ROLE.BUYER;
    const stats = [
      formatMoney(row.team_balance),
      formatMoney(spendLoaded ? (buyerSpend.get(row.uid) ?? 0) : undefined),
      cardsLoaded ? String(cardFinancials?.cardsCount ?? 0) : "—",
      formatMoney(cardsLoaded ? (cardFinancials?.cardsBalance ?? 0) : undefined),
    ];
    const joined = row.created_at
      ? new Date(row.created_at).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "2-digit" })
      : null;
    return (
      <TeamMemberCard
        key={row.uid}
        name={row.name}
        email={row.email}
        role={row.company_role}
        status={row.status}
        joined={joined}
        stats={isBuyer ? MEMBER_STATS.map((label, index) => ({ label, value: stats[index] })) : undefined}
        onOpen={() => handleUserClick(row.uid)}
        onTopUp={showTopUp ? () => handleTopUp(row) : undefined}
        onRemove={() => handleDisableUser(row.uid)}
      />
    );
  };

  const centeredMessage = (
    message: string,
    options?: { hint?: string; action?: React.ReactNode },
  ) => (
    <VBox
      gap="12px"
      sx={{ alignItems: "center", textAlign: "center", padding: "44px 16px 40px" }}
    >
      <Box
        aria-hidden
        sx={{
          width: "52px",
          height: "52px",
          borderRadius: "16px",
          background: DS.accentSoft,
          color: DS.accent,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      </Box>
      <VBox gap="4px">
        <Typo sx={{ fontSize: "15px", fontWeight: 650, color: DS.ink }}>{message}</Typo>
        {options?.hint && (
          <Typo sx={{ fontSize: "13px", color: DS.muted }}>{options.hint}</Typo>
        )}
      </VBox>
      {options?.action}
    </VBox>
  );

  return (
    <Page sx={{ boxShadow: "none" }}>
      <VBox gap="16px">
        <EntityHeader
          leading={<Avatar name={team.name} size={52} />}
          title={team.name}
          badge={<StatusBadge status={team.status || "unknown"} />}
          meta={[
            createdDate && `Created ${createdDate}`,
            team.uid && `ID ${team.uid.substring(0, 12)}…`,
          ]
            .filter(Boolean)
            .join(" · ")}
        />

        <Stage>
          <VBox gap="18px" sx={{ minWidth: 0, padding: "4px 4px 4px 8px", [DS.mobile]: { padding: 0 } }}>
            <VBox gap="6px">
              <Typo sx={{ fontSize: "12px", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: DS.muted }}>
                Team balance
              </Typo>
              <Typo sx={{ fontSize: "44px", lineHeight: 1.05, fontWeight: 700, letterSpacing: "-0.035em", color: DS.ink, fontVariantNumeric: "tabular-nums", overflowWrap: "anywhere", [DS.mobile]: { fontSize: "36px" } }}>
                {formatMoney(teamStats.totalBalance)}
              </Typo>
              <Typo sx={{ fontSize: "13px", color: DS.muted }}>
                {[
                  `Combined available balance of ${teamStats.buyersCount} ${teamStats.buyersCount === 1 ? "buyer" : "buyers"}`,
                  spendLoaded && `${formatMoney(topSpendersTotal, topSpendersCurrency)} spent`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Typo>
            </VBox>
            {canManageTeam && (
              <HBox gap="10px" sx={{ flexWrap: "wrap" }}>
                <StageButton onClick={handleAddMember}>
                  <svg aria-hidden width="16" height="16" viewBox="0 0 24 24" {...stroke}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M19 8v6M22 11h-6" /></svg>
                  Add Member
                </StageButton>
                <StageButton ghost onClick={handleEditTeam}>
                  <svg aria-hidden width="16" height="16" viewBox="0 0 24 24" {...stroke}><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" /></svg>
                  Edit Team
                </StageButton>
              </HBox>
            )}
          </VBox>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "8px" }}>
            <StageStat label="Members">
              {members.length}
              <Box element="span" sx={{ display: "block", marginTop: "2px", fontSize: "11px", fontWeight: 500, color: DS.muted }}>{`${activeMembers} active`}</Box>
            </StageStat>
            <StageStat label="Buyers">{teamStats.buyersCount}</StageStat>
            <StageStat label="Team leads">{teamStats.teamLeadsCount}</StageStat>
            <StageStat label="Top buyer">
              {teamStats.mostMoneyUser ? (
                <>
                  <Box element="span" sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}>{teamStats.mostMoneyUser.name}</Box>
                  <Box element="span" sx={{ display: "block", marginTop: "2px", fontSize: "11px", fontWeight: 500, color: DS.muted }}>{formatMoney(teamStats.maxBalance)}</Box>
                </>
              ) : "—"}
            </StageStat>
          </Box>
        </Stage>

        {/* ─── Members ──────────────────────────────────────────────── */}
        <Box sx={{ minWidth: 0, containerType: "inline-size" }}>
          <Section
            title="Team members"
            action={
              <Typo
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: DS.accentInk,
                  background: DS.accentSoft,
                  borderRadius: "999px",
                  padding: "2px 10px",
                }}
              >
                {filteredMembers.length}
              </Typo>
            }
          >
            <HBox
              gap="10px"
              sx={{ alignItems: "center", flexWrap: "wrap", padding: "4px 0 12px" }}
            >
              <Box sx={{ minWidth: 0, flex: "1 1 220px" }}>
                <Input
                  placeholder="Search by name or email"
                  data={filter}
                  name="search"
                  type="search"
                  autoComplete="off"
                  onChange={(value: string) =>
                    setFilter((prev) => ({ ...prev, search: value }))
                  }
                  size="small"
                />
              </Box>
              <Box sx={{ minWidth: 0, flex: "0 0 150px", alignSelf: "flex-end" }}>
                <Select
                  data={filter}
                  name="role"
                  ariaLabel="Filter team members by role"
                  values={[
                    { name: "All roles", value: "All" },
                    { name: "Admin", value: USER_ROLE.ADMIN },
                    { name: "Team lead", value: USER_ROLE.TEAM_LEAD },
                    { name: "Buyer", value: USER_ROLE.BUYER },
                  ]}
                  onChange={(value: string) =>
                    setFilter((prev) => ({ ...prev, role: value }))
                  }
                  size="small"
                />
              </Box>
            </HBox>

            {membersBusy && members.length === 0 ? (
              <Typo
                sx={{ color: DS.muted, fontSize: "14px", textAlign: "center", padding: "40px 0" }}
              >
                Loading members…
              </Typo>
            ) : filteredMembers.length === 0 ? (
              hasActiveFilter ? (
                centeredMessage("No members match your filters", {
                  hint: "Try a different name, email or role.",
                  action: (
                    <ActionButton
                      tone="secondary"
                      onClick={() => setFilter({ search: "", role: "All" })}
                    >
                      Clear filters
                    </ActionButton>
                  ),
                })
              ) : (
                centeredMessage("No members yet", {
                  hint: "Add a user to give them access to this team.",
                  action: canManageTeam ? (
                    <ActionButton tone="primary" onClick={handleAddMember}>
                      Add first member
                    </ActionButton>
                  ) : undefined,
                })
              )
            ) : (
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "12px", padding: "0 0 16px", [DS.mobile]: { gridTemplateColumns: "minmax(0, 1fr)" } }}>
                {filteredMembers.map(renderMemberRow)}
              </Box>
            )}
          </Section>
        </Box>

        {/* ─── Recent activity ──────────────────────────────────────── */}
        <Section title="Recent activity">
          {teamTransactionsBusy ? (
            <Typo
              sx={{ color: DS.muted, fontSize: "14px", textAlign: "center", padding: "32px 0" }}
            >
              Loading activity…
            </Typo>
          ) : recentTransactions.length === 0 ? (
            <VBox
              gap="8px"
              sx={{ alignItems: "center", padding: "32px 0" }}
            >
              <IconInfo width={32} height={32} color="#CBD5E1" />
              <Typo sx={{ color: DS.muted, fontSize: "14px" }}>No recent activity</Typo>
            </VBox>
          ) : (
            <VBox gap="0">
              {recentTransactions.slice(0, showAllRecent ? 8 : 3).map((tx) => {
                const income = tx.direction === "income";
                return (
                  <HBox
                    key={tx.id}
                    sx={{
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "12px",
                      padding: "12px 0",
                      borderTop: `1px solid ${DS.line}`,
                      "&:first-of-type": { borderTop: 0 },
                    }}
                  >
                    <HBox gap="12px" sx={{ alignItems: "center", minWidth: 0 }}>
                      <Box
                        aria-hidden
                        sx={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "11px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flex: "0 0 36px",
                          backgroundColor: income ? "#ECFDF5" : "#FEF2F2",
                          color: income ? "#047857" : "#B42318",
                          fontWeight: 700,
                          fontSize: "16px",
                        }}
                      >
                        {income ? "+" : "−"}
                      </Box>
                      <VBox gap="2px" sx={{ minWidth: 0 }}>
                        <Typo
                          sx={{
                            fontWeight: 600,
                            color: DS.ink,
                            fontSize: "14px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {getTransactionDisplayLabel(tx) || "Transaction"}
                        </Typo>
                        <Typo sx={{ color: DS.muted, fontSize: "12px" }}>
                          {new Date(tx.created_at).toLocaleString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </Typo>
                      </VBox>
                    </HBox>
                    <VBox gap="4px" sx={{ alignItems: "flex-end", flexShrink: 0 }}>
                      <Typo
                        sx={{
                          fontWeight: 650,
                          fontSize: "14px",
                          fontVariantNumeric: "tabular-nums",
                          color: income ? "#047857" : "#B42318",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {income ? "+" : "−"}
                        {formatCurrencyWithNumberToSymbolWithNumber(
                          tx.amount,
                          tx.currency || issuerCurrency,
                        )}
                      </Typo>
                      <StatusBadge status={tx.status.toLowerCase()} />
                    </VBox>
                  </HBox>
                );
              })}
              {recentTransactions.length > 3 && (
                <HBox gap="8px" sx={{ justifyContent: "center", paddingTop: "8px", borderTop: `1px solid ${DS.line}` }}>
                  <ActionButton onClick={() => setShowAllRecent((value) => !value)}>
                    {showAllRecent ? "Show less" : `Show more (${Math.min(recentTransactions.length, 8) - 3})`}
                  </ActionButton>
                  {showAllRecent && (
                    <ActionButton onClick={() => router.navigate("transactions", undefined, { teamId: team.uid })}>
                      View all transactions →
                    </ActionButton>
                  )}
                </HBox>
              )}
            </VBox>
          )}
        </Section>

        {/* ─── Money Inflow Chart ───────────────────────────────────── */}
        <Box sx={CHART_PANEL_SX}>
          <Box
            data-chart-header="money-inflow"
            sx={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "18px minmax(0, 1fr)"
                : "18px minmax(0, 1fr) auto",
              alignItems: "center",
              columnGap: "8px",
              rowGap: isMobile ? "12px" : 0,
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke={TEAM_DETAILS_BRAND}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
              <polyline points="16 7 22 7 22 13" />
            </svg>
            <Typo
              variant="headMedium"
              sx={{
                color: "#111827",
                fontWeight: 700,
                fontSize: "1.05rem",
                letterSpacing: "-0.01em",
              }}
            >
              Funds inflow, last {analyticsDays} days
            </Typo>
            <HBox
              data-chart-header-controls
              sx={{
                gridColumn: isMobile ? "1 / -1" : "3",
                alignItems: "center",
                justifyContent: isMobile ? "space-between" : "flex-end",
                gap: "10px",
                minWidth: 0,
              }}
            >
              <Typo
                variant="medium"
                sx={{ color: TEAM_DETAILS_BRAND, fontWeight: 700 }}
              >
                {formatCurrencyWithNumberToSymbolWithNumber(
                  moneyInflowTotal,
                  moneyInflowCurrency,
                )}
              </Typo>
              {renderSegmentedControl(
                TEAM_ANALYTICS_DAY_OPTIONS,
                analyticsDays,
                setAnalyticsDays,
                "d",
              )}
            </HBox>
          </Box>
          <Box sx={{ width: "100%", height: hasMoneyInflow || moneyInflowBusy ? "220px" : "110px" }}>
            {moneyInflowBusy ? (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typo variant="medium" sx={{ color: "#9CA3AF" }}>
                  Loading chart...
                </Typo>
              </Box>
            ) : moneyInflowResponse?.error ? (
              renderChartMessage(
                getFriendlyErrorMessage(
                  moneyInflowResponse.error,
                  "Failed to load money inflow",
                ),
              )
            ) : !hasMoneyInflow ? (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <IconInfo width={32} height={32} color="#D1D5DB" />
                <Typo variant="medium" sx={{ color: "#9CA3AF" }}>
                  No incoming transfers in the last {analyticsDays} days
                </Typo>
              </Box>
            ) : (
              <PositiveSizeChart>
                <AreaChart
                  data={chartData}
                  margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient
                      id="tdpColorAmount"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor={TEAM_DETAILS_BRAND}
                        stopOpacity={0.15}
                      />
                      <stop
                        offset="95%"
                        stopColor={TEAM_DETAILS_BRAND}
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#F3F4F6"
                  />
                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#9CA3AF", fontSize: 11 }}
                    dy={8}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#9CA3AF", fontSize: 11 }}
                    tickFormatter={(v) =>
                      formatCurrencyWithNumberToSymbolWithNumber(
                        v,
                        moneyInflowCurrency,
                      )
                    }
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#FFFFFF",
                      border: "1px solid #E5E7EB",
                      borderRadius: "10px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      fontSize: "13px",
                    }}
                    formatter={(value: number | string | undefined) => [
                      `${formatCurrencyWithNumberToSymbolWithNumber(Number(value) || 0, moneyInflowCurrency)}`,
                      "Amount",
                    ]}
                    cursor={{
                      stroke: TEAM_DETAILS_BRAND,
                      strokeWidth: 1,
                      strokeDasharray: "4 2",
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="amount"
                    stroke={TEAM_DETAILS_BRAND}
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#tdpColorAmount)"
                    dot={false}
                    activeDot={{
                      r: 5,
                      fill: TEAM_DETAILS_BRAND,
                      stroke: "#fff",
                      strokeWidth: 2,
                    }}
                  />
                </AreaChart>
              </PositiveSizeChart>
            )}
          </Box>
        </Box>

        {/* ─── Top Spenders + Transaction Status ───────────────────── */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns:
              viewportWidth <= 960 ? "minmax(0, 1fr)" : "2fr 1fr",
            gap: isMobile ? "16px" : "24px",
          }}
        >
          {/* Top Spenders Bar Chart */}
          <Box sx={CHART_PANEL_SX}>
            <Box
              data-chart-header="top-spenders"
              sx={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "18px minmax(0, 1fr)"
                  : "18px minmax(0, 1fr) auto",
                alignItems: "center",
                columnGap: "8px",
                rowGap: isMobile ? "12px" : 0,
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke={TEAM_DETAILS_BRAND}
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="20" x2="18" y2="10" />
                <line x1="12" y1="20" x2="12" y2="4" />
                <line x1="6" y1="20" x2="6" y2="14" />
              </svg>
              <Typo
                variant="headMedium"
                sx={{
                  color: "#111827",
                  fontWeight: 700,
                  fontSize: "1.05rem",
                  letterSpacing: "-0.01em",
                }}
              >
                Top spenders
              </Typo>
              <HBox
                data-chart-header-controls
                sx={{
                  gridColumn: isMobile ? "1 / -1" : "3",
                  alignItems: "center",
                  justifyContent: isMobile ? "space-between" : "flex-end",
                  gap: "10px",
                  minWidth: 0,
                }}
              >
                <Typo
                  variant="medium"
                  sx={{ color: TEAM_DETAILS_BRAND, fontWeight: 700 }}
                >
                  {formatCurrencyWithNumberToSymbolWithNumber(
                    topSpendersTotal,
                    topSpendersCurrency,
                  )}
                </Typo>
                {renderSegmentedControl(
                  TEAM_TOP_SPENDERS_LIMIT_OPTIONS,
                  topSpendersLimit,
                  setTopSpendersLimit,
                  "",
                )}
              </HBox>
            </Box>
            <Box sx={{ width: "100%", height: hasTopSpendersData || topSpendersBusy ? "220px" : "110px" }}>
              {topSpendersBusy ? (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typo variant="medium" sx={{ color: "#9CA3AF" }}>
                    Loading chart...
                  </Typo>
                </Box>
              ) : topSpendersResponse?.error ? (
                renderChartMessage(
                  getFriendlyErrorMessage(
                    topSpendersResponse.error,
                    "Failed to load top spenders",
                  ),
                )
              ) : !hasTopSpendersData ? (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <IconInfo width={32} height={32} color="#D1D5DB" />
                  <Typo variant="medium" sx={{ color: "#9CA3AF" }}>
                    No data yet
                  </Typo>
                </Box>
              ) : (
                <PositiveSizeChart>
                  <BarChart
                    data={spendingData}
                    layout="vertical"
                    margin={{ top: 5, right: 20, left: 40, bottom: 5 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      horizontal={false}
                      stroke="#F3F4F6"
                    />
                    <XAxis type="number" hide />
                    <YAxis
                      dataKey="name"
                      type="category"
                      width={90}
                      tick={{ fill: "#6B7280", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      cursor={{ fill: "#F8FAFC" }}
                      contentStyle={{
                        backgroundColor: "#FFFFFF",
                        border: "1px solid #E5E7EB",
                        borderRadius: "10px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                        fontSize: "13px",
                      }}
                      formatter={(value: number | string | undefined) => [
                        `${formatCurrencyWithNumberToSymbolWithNumber(Number(value) || 0, topSpendersCurrency)}`,
                        "Spent",
                      ]}
                    />
                    <Bar
                      dataKey="amount"
                      fill={TEAM_DETAILS_BRAND}
                      radius={[0, BAR_RADIUS, BAR_RADIUS, 0]}
                      barSize={18}
                      onClick={(entry: unknown) => {
                        const point = entry as {
                          userUid?: string;
                          payload?: { userUid?: string };
                        };
                        const userUid = point.userUid || point.payload?.userUid;
                        if (userUid) void handleUserClick(userUid);
                      }}
                      style={{ cursor: "pointer" }}
                    />
                  </BarChart>
                </PositiveSizeChart>
              )}
            </Box>
          </Box>

          {/* Transaction Status Pie Chart */}
          <Box sx={CHART_PANEL_SX}>
            <HBox sx={{ alignItems: "center", gap: "8px", rowGap: "10px", flexWrap: "wrap" }}>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke={TEAM_DETAILS_BRAND}
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
                <path d="M22 12A10 10 0 0 0 12 2v10z" />
              </svg>
              <Typo
                variant="headMedium"
                sx={{
                  color: "#111827",
                  fontWeight: 700,
                  fontSize: "1.05rem",
                  letterSpacing: "-0.01em",
                  whiteSpace: "nowrap",
                  flex: "1 1 auto",
                }}
              >
                Transaction status
              </Typo>

              {renderSegmentedControl(
                TEAM_ANALYTICS_DAY_OPTIONS,
                analyticsDays,
                setAnalyticsDays,
                "d",
              )}

              <HBox sx={{ alignItems: "center" }} gap="8px">
                <Box
                  element="button"
                  type="button"
                  aria-label={`View ${transactionStatusTotalCount} team transactions`}
                  onClick={() => router.navigate("transactions", undefined, { teamId: team.uid, days: String(analyticsDays) })}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "4px 8px",
                    margin: "-4px -8px",
                    border: 0,
                    borderRadius: "8px",
                    background: "transparent",
                    color: TEAM_DETAILS_BRAND,
                    fontFamily: "inherit",
                    fontSize: "15px",
                    fontWeight: 700,
                    cursor: "pointer",
                    textDecoration: "underline",
                    textDecorationColor: "rgba(43, 92, 138, 0.35)",
                    textUnderlineOffset: "3px",
                    "&:hover": { background: DS.accentSoft, textDecorationColor: "currentColor" },
                    "&:focus-visible": { outline: `2px solid ${DS.accent}`, outlineOffset: "2px" },
                  }}
                >
                  {transactionStatusTotalCount} tx →
                </Box>
                <Typo
                  variant="caption"
                  sx={{ color: TEAM_DETAILS_MUTED, fontWeight: 600 }}
                >
                  (
                  {formatCurrencyWithNumberToSymbolWithNumber(
                    transactionStatusTotalAmount,
                    transactionStatusCurrency,
                  )}
                  )
                </Typo>
              </HBox>
            </HBox>

            <Box sx={{ width: "100%", height: hasTransactionStatusData || transactionStatusBusy ? "220px" : "110px" }}>
              {transactionStatusBusy ? (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typo variant="medium" sx={{ color: "#9CA3AF" }}>
                    Loading chart...
                  </Typo>
                </Box>
              ) : transactionStatusResponse?.error ? (
                renderChartMessage(
                  getFriendlyErrorMessage(
                    transactionStatusResponse.error,
                    "Failed to load transaction status",
                  ),
                )
              ) : !hasTransactionStatusData ? (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <IconInfo width={32} height={32} color="#D1D5DB" />
                  <Typo variant="medium" sx={{ color: "#9CA3AF" }}>
                    No data yet
                  </Typo>
                </Box>
              ) : (
                <PositiveSizeChart>
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={75}
                      paddingAngle={4}
                      dataKey="value"
                    >
                      {statusData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.color}
                          stroke="none"
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#FFFFFF",
                        border: "1px solid #E5E7EB",
                        borderRadius: "10px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                        fontSize: "13px",
                      }}
                      formatter={(
                        value: number | string | undefined,
                        _name,
                        item,
                      ) => [
                        `${Number(value || 0)} tx / ${formatCurrencyWithNumberToSymbolWithNumber(Number(item?.payload?.amount || 0), transactionStatusCurrency)}`,
                        "Status",
                      ]}
                    />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      iconType="circle"
                      iconSize={8}
                      wrapperStyle={{ fontSize: "12px", color: "#6B7280" }}
                    />
                  </PieChart>
                </PositiveSizeChart>
              )}
            </Box>
          </Box>
        </Box>

        {/* ─── Declined Transactions By Buyer ───────────────────────── */}
        <Box sx={CHART_PANEL_SX}>
          <HBox
            sx={{
              alignItems: "center",
              justifyContent: "space-between",
              gap: "16px",
              flexWrap: "wrap",
            }}
          >
            <HBox sx={{ alignItems: "center", gap: "8px" }}>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke={TEAM_DETAILS_BRAND}
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 3v18h18" />
                <path d="m7 16 4-4 3 3 5-7" />
              </svg>
              <VBox gap="2px">
                <Typo
                  variant="headMedium"
                  sx={{
                    color: "#111827",
                    fontWeight: 700,
                    fontSize: "1.05rem",
                    letterSpacing: 0,
                  }}
                >
                  Declined transactions
                </Typo>
                <Typo
                  variant="caption"
                  sx={{ color: TEAM_DETAILS_MUTED, fontSize: "0.78rem" }}
                >
                  Daily count by buyer over the last {analyticsDays} days
                </Typo>
              </VBox>
            </HBox>

            <HBox sx={{ alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              <Typo
                variant="medium"
                sx={{ color: TEAM_DETAILS_BRAND, fontWeight: 700 }}
              >
                {visibleDeclinedBuyerTotal} declined
              </Typo>
              {renderSegmentedControl(
                TEAM_ANALYTICS_DAY_OPTIONS,
                analyticsDays,
                setAnalyticsDays,
                "d",
              )}
            </HBox>
          </HBox>

          {declinedByBuyerBusy ? (
            <Box
              sx={{
                width: "100%",
                height: "260px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typo variant="medium" sx={{ color: "#9CA3AF" }}>
                Loading chart...
              </Typo>
            </Box>
          ) : declinedByBuyerResponse?.error ? (
            <Box sx={{ height: "260px" }}>
              {renderChartMessage(
                getFriendlyErrorMessage(
                  declinedByBuyerResponse.error,
                  "Failed to load declined transactions",
                ),
              )}
            </Box>
          ) : !hasDeclinedBuyerData ? (
            <Box sx={{ height: "110px" }}>
              {renderChartMessage("No declined transactions available")}
            </Box>
          ) : (
            <>
              <HBox sx={{ flexWrap: "wrap", gap: "8px" }}>
                {declinedBuyerChart.series.map((series) => {
                  const hidden = !!declinedBuyerHidden[series.key];
                  return (
                    <HBox
                      key={series.key}
                      onClick={() => toggleDeclinedBuyerSeries(series.key)}
                      sx={{
                        alignItems: "center",
                        gap: "6px",
                        padding: "6px 9px",
                        borderRadius: "8px",
                        border: `1px solid ${hidden ? TEAM_DETAILS_BORDER : series.color}`,
                        backgroundColor: hidden ? TEAM_DETAILS_SOFT : "#ffffff",
                        cursor: "pointer",
                        opacity: hidden ? 0.65 : 1,
                        transition: "all 0.15s ease",
                        "&:hover": {
                          borderColor: series.color,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: hidden ? "#CBD5E1" : series.color,
                        }}
                      />
                      <Typo
                        variant="caption"
                        sx={{
                          color: hidden ? "#94a3b8" : TEAM_DETAILS_TEXT,
                          fontSize: "0.72rem",
                          fontWeight: 650,
                        }}
                      >
                        {series.name}
                      </Typo>
                      <Typo
                        variant="caption"
                        sx={{
                          color: hidden ? "#CBD5E1" : TEAM_DETAILS_MUTED,
                          fontSize: "0.68rem",
                          fontWeight: 600,
                        }}
                      >
                        {series.total}
                      </Typo>
                    </HBox>
                  );
                })}
              </HBox>

              <Box sx={{ width: "100%", height: "260px" }}>
                {visibleDeclinedBuyerSeries.length === 0 ? (
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typo variant="medium" sx={{ color: "#9CA3AF" }}>
                      Select at least one buyer
                    </Typo>
                  </Box>
                ) : (
                  <PositiveSizeChart>
                    <LineChart
                      data={declinedBuyerChart.daily}
                      margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#F3F4F6"
                      />
                      <XAxis
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#9CA3AF", fontSize: 11 }}
                        dy={8}
                        minTickGap={18}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#9CA3AF", fontSize: 11 }}
                        width={32}
                        allowDecimals={false}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#FFFFFF",
                          border: "1px solid #E5E7EB",
                          borderRadius: "10px",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                          color: "#111827",
                          fontSize: "13px",
                        }}
                        itemStyle={{
                          color: "#111827",
                          fontWeight: 600,
                          fontSize: "12px",
                        }}
                        labelStyle={{
                          color: TEAM_DETAILS_MUTED,
                          fontSize: "11px",
                          marginBottom: "4px",
                          fontWeight: 500,
                        }}
                        formatter={(
                          value: number | string | undefined,
                          name,
                        ) => [Number(value || 0), name]}
                        labelFormatter={(_label, payload) =>
                          `Date: ${payload?.[0]?.payload?.fullDate || _label}`
                        }
                      />
                      {visibleDeclinedBuyerSeries.map((series) => (
                        <Line
                          key={series.key}
                          type="monotone"
                          dataKey={series.key}
                          name={series.name}
                          stroke={series.color}
                          strokeWidth={2.25}
                          dot={{ r: 2.5, fill: series.color, strokeWidth: 0 }}
                          activeDot={{
                            r: 4,
                            strokeWidth: 0,
                            fill: series.color,
                          }}
                        />
                      ))}
                    </LineChart>
                  </PositiveSizeChart>
                )}
              </Box>
            </>
          )}
        </Box>

      </VBox>
    </Page>
  );
};
